import React, { useState } from 'react';
import { enviarContacto, consultarEstadoAfiliacion, verificarDocumento, getEstadoPagoInscripcion } from '../services/ecolabApi';
import {
  Box, Container, Grid, Typography, Button, TextField, Card, CardContent,
  Chip, Alert, Snackbar, Divider, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  List, ListItem, ListItemIcon, ListItemText,
  Stepper, Step, StepLabel, StepContent,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import CloseIcon         from '@mui/icons-material/Close';
import HelpOutlineIcon   from '@mui/icons-material/HelpOutline';
import VideocamIcon      from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon        from '@mui/icons-material/Groups';
import WhatsAppIcon      from '@mui/icons-material/WhatsApp';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon  from '@mui/icons-material/InfoOutlined';
import PageHero from '../components/PageHero';
import ContratoAfiliacion      from '../components/ContratoAfiliacion';
import PagoInscripcionWidget   from '../components/PagoInscripcionWidget';
import { guardarDocumentoAfiliado, registrarSesionOrientacion, reenviarConfirmacionEmail } from '../services/authApi';
import BlockIcon from '@mui/icons-material/Block';
import BadgeIcon from '@mui/icons-material/Badge';
import VerificadorDocumento from '../components/VerificadorDocumento';
import TurnstileWidget      from '../components/TurnstileWidget';

export function NuestraRed() {
  const regions = [
    { name: 'Antioquia', cities: ['Medellín', 'Bello', 'Itagüí', 'Envigado', 'Rionegro'], type: 'Hub Principal' },
    { name: 'Cundinamarca', cities: ['Bogotá', 'Soacha', 'Facatativá', 'Zipaquirá'], type: 'Hub Regional' },
    { name: 'Valle del Cauca', cities: ['Cali', 'Buenaventura', 'Palmira', 'Tuluá'], type: 'En desarrollo' },
    { name: 'Santander', cities: ['Bucaramanga', 'Barrancabermeja', 'Socorro', 'Girón'], type: 'En desarrollo' },
    { name: 'Córdoba', cities: ['Montería', 'Cereté', 'Lorica', 'Sahagún'], type: 'En desarrollo' },
    { name: 'Nariño', cities: ['Pasto', 'Túquerres', 'La Unión', 'Ipiales'], type: 'En desarrollo' },
  ];

  const allies = [
    { name: 'Holdin Socioempresarial Solidario', role: 'Alianza Estratégica Nacional', url: 'https://holdinsocioempresariasolidario.org/' },
    { name: 'ECOSO', role: 'Economía Solidaria' },
    { name: 'MERCOSUR', role: 'Cooperación Internacional' },
    { name: 'FAO Colombia', role: 'Seguridad Alimentaria' },
  ];

  return (
    <Box>
      <PageHero
        title="Nuestra Red"
        subtitle="Un ecosistema colaborativo que se extiende por todo el territorio colombiano."
        breadcrumbs={[{ label: 'Nuestra Red' }]}
      />

      <Box sx={{ py: { xs: 8, md: 14 }, background: '#f8f5ef' }}>
        <Container maxWidth="xl">
          {/* Structure */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 1, display: 'block' }}>Estructura Federativa</Typography>
            <Typography variant="h3" sx={{ color: '#1a1a1a', mb: 3, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Red de Segundo Nivel
            </Typography>
            <Typography variant="body1" sx={{ color: '#4a5568', maxWidth: 600, mx: 'auto' }}>
              FENAGRO APL opera como federación de segundo nivel, articulando organizaciones solidarias de primer nivel 
              y comunidades de prosumidores a lo largo y ancho de Colombia.
            </Typography>
          </Box>

          {/* Levels */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            {[
              { level: 'II', title: 'FENAGRO APL', role: 'Federación — Segundo Nivel', desc: 'Coordinación estratégica, banco de proyectos, innovación, mercados y formación.', bg: 'linear-gradient(135deg, #1a4d2e, #0e2a18)' },
              { level: 'I', title: 'Asociaciones y Cooperativas', role: 'Organizaciones — Primer Nivel', desc: 'Cooperativas agropecuarias, asociaciones de productores y grupos empresariales solidarios.', bg: 'linear-gradient(135deg, #2d5a1a, #1a4d2e)' },
              { level: '∞', title: 'Comunidad Prosumidores', role: 'Base de la Red', desc: 'Productores, comercializadores y consumidores que conforman la economía colaborativa.', bg: 'linear-gradient(135deg, #c8a84b, #9c7e32)' },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{ p: 4, background: item.bg, borderRadius: 3, textAlign: 'center', height: '100%' }}>
                  <Typography sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '3.5rem', fontWeight: 900,
                    color: 'rgba(255,255,255,0.2)', lineHeight: 1, mb: 2,
                  }}>
                    {item.level}
                  </Typography>
                  <Typography sx={{ fontFamily: "'Playfair Display', serif", color: '#ffffff', fontSize: '1.3rem', fontWeight: 700, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif", mb: 2 }}>
                    {item.role}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', lineHeight: 1.65 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Regions */}
          <Typography variant="h4" sx={{ mb: 4, color: '#1a1a1a', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Presencia Regional
          </Typography>
          <Grid container spacing={3} sx={{ mb: 8 }}>
            {regions.map((region, i) => (
              <Grid item xs={12} sm={6} lg={4} key={i}>
                <Card sx={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', color: '#1a1a1a' }}>{region.name}</Typography>
                      <Chip
                        label={region.type}
                        size="small"
                        sx={{
                          background: region.type === 'Hub Principal' ? '#1a4d2e' : region.type === 'Hub Regional' ? '#c8a84b15' : '#f0ebe0',
                          color: region.type === 'Hub Principal' ? '#fff' : region.type === 'Hub Regional' ? '#9c7e32' : '#718096',
                          fontSize: '0.68rem', fontWeight: 600,
                          border: region.type === 'Hub Regional' ? '1px solid #c8a84b50' : 'none',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                      {region.cities.map(city => (
                        <Typography key={city} sx={{ fontSize: '0.8rem', color: '#718096', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <LocationOnIcon sx={{ fontSize: 12 }} />{city}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Allies */}
          <Typography variant="h4" sx={{ mb: 4, color: '#1a1a1a', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Alianzas Estratégicas
          </Typography>
          <Grid container spacing={3}>
            {allies.map((ally, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  p: 3, background: '#ffffff', borderRadius: 3, textAlign: 'center',
                  border: '1px solid rgba(200,168,75,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': { borderColor: '#c8a84b', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' },
                }}>
                  <Typography variant="h6" sx={{ fontSize: '0.95rem', mb: 0.8, color: '#1a1a1a' }}>{ally.name}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#718096', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif" }}>
                    {ally.role}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export function Afiliacion() {
  const [contratoOpen, setContratoOpen] = useState(false);
  // Cooperativa fija si viene por URL param ?coop=ID (para embeber en sitio de cooperativa)
  const cooperativaIdFijo = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('coop');
    return v ? parseInt(v, 10) : null;
  }, []);
  const [preVerifOpen, setPreVerifOpen]   = useState(false);
  const [pagoConfirmado, setPagoConfirmado] = useState(false);
  const [preVerifDoc,  setPreVerifDoc]    = useState('');
  const [preVerifState, setPreVerifState] = useState('idle');
  const [tsTokenPreVerif, setTsTokenPreVerif] = useState('');
  const [afiliadoEnCurso,     setAfiliadoEnCurso]     = useState(false); // mensaje tras detectar solicitud existente
  const [guiaOpen,    setGuiaOpen]    = useState(false);
  const [guiaPaso,    setGuiaPaso]    = useState(0);
  // Documentos subidos en la guía: índice → { file, url, valido }
  const [docsGuia,    setDocsGuia]    = useState({});
  const setDocGuia = (idx, data) => setDocsGuia(prev => ({ ...prev, [idx]: data }));
  const todosSubidos = Object.keys(docsGuia).length === 6 && Object.values(docsGuia).every(d => d.valido);
  const [notifState, setNotifState] = useState('idle'); // idle | loading | sent | error
  // Solicitud confirmada: se activa cuando el usuario envía el formulario
  // o cuando consulta su estado y ya existe una solicitud
  const [solicitudInfo, setSolicitudInfo] = useState(null);
  const [afiliadoIdActual, setAfiliadoIdActual] = useState(null);
  const [autoConsultar, setAutoConsultar] = useState(null); // cédula para consulta automática desde pre-verificación
  // Reenvío del correo de confirmación de email
  const [reenvioEmailState, setReenvioEmailState] = useState('idle'); // idle | enviando | enviado | error
  const [reenvioEmailMsg, setReenvioEmailMsg]     = useState('');
  const [tsTokenReenvio, setTsTokenReenvio]       = useState('');

  const benefits = [
    'Acceso al Banco de Proyectos Agroindustrial',
    'Participación en Escuelas de Emprendimiento APL',
    'Integración a Redes Empresariales Solidarias',
    'Mercado Asegurado para sus productos',
    'Asesoría jurídica y empresarial especializada',
    'Acceso a convocatorias de financiamiento público y privado',
    'Formación continua y transferencia de conocimiento',
    'Red de contactos agroindustriales nacionales e internacionales',
    'Apoyo en marketing digital y comercio electrónico',
    'Asesoría en TIC y transformación digital',
  ];

  const steps = [
    {
      num: 1,
      title: 'Solicitud Inicial',
      desc: 'Complete el contrato de afiliación digital con sus datos personales, beneficiarios y modalidad de aporte.',
      isForm: true,
      boton: { label: 'Iniciar Solicitud →', accion: 'contrato', variante: 'outlined' },
    },
    {
      num: 2,
      title: 'Evaluación',
      desc: 'Nuestro equipo evalúa su perfil y los objetivos de su organización o emprendimiento. Recibirá respuesta en máximo 48 horas.',
      boton: { label: 'Consultar mi estado', accion: 'estado', variante: 'outlined' },
    },
    {
      num: 3,
      title: 'Orientación',
      desc: 'Sesión de inducción para conocer los beneficios, compromisos y servicios disponibles. Agéndela por WhatsApp o correo.',
      boton: { label: 'Agendar sesión de orientación', accion: 'agendar', variante: 'outlined' },
    },
    {
      num: 4,
      title: 'Vinculación',
      desc: 'Formalización del proceso de afiliación y acceso a todos los beneficios de la red.',
      boton: { label: 'Completar envío de documentos', accion: 'guia', variante: 'outlined' },
    },
  ];

  const verificarElegibilidad = async () => {
    if (preVerifDoc.trim().length < 5) return;
    setPreVerifState('checking');
    try {
      const data = await verificarDocumento(preVerifDoc.trim(), cooperativaIdFijo || undefined, tsTokenPreVerif);
      if (data.excluido) {
        setPreVerifState('excluido');
      } else if (!data.disponible) {
        setPreVerifOpen(false);
        setPreVerifState('idle');
        setAfiliadoEnCurso(true);
        setTimeout(() => {
          document.getElementById('consulta-estado')?.scrollIntoView({ behavior:'smooth', block:'start' });
        }, 300);
      } else {
        setPreVerifState('ok');
      }
    } catch(e) {
      setPreVerifState('ok');
    }
  };

  return (
    <Box>
      <PageHero
        title="Afiliación"
        subtitle="Únase a la red solidaria que está transformando el agro colombiano."
        breadcrumbs={[{ label: 'Afiliación' }]}
      />

      <Box sx={{ py: { xs: 8, md: 14 }, background: '#f8f5ef' }}>
        <Container maxWidth="xl">
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 2, display: 'block' }}>¿Por qué afiliarse?</Typography>
              <Typography variant="h3" sx={{ mb: 3, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#1a1a1a' }}>
                Beneficios de pertenecer a FENAGRO
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 4 }}>
                Al afiliarse a FENAGRO APL, su organización o emprendimiento accede a un ecosistema completo de 
                bienes y servicios diseñados para potenciar su crecimiento en el marco de la economía solidaria colombiana.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {benefits.map((b, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#1a4d2e', fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#2d3748' }}>{b}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 2, display: 'block' }}>Proceso</Typography>
              <Typography variant="h4" sx={{ mb: 4, color: '#1a1a1a', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                ¿Cómo afiliarme?
              </Typography>

              {/* Banner CRITICO: correo no confirmado bloquea siguientes pasos */}
              {solicitudInfo && solicitudInfo.emailConfirmado === false && (
                <Box sx={{
                  mb: 3, p: 2.5, borderRadius: 2,
                  background: 'linear-gradient(135deg, #fff8e1, #ffecb3)',
                  border: '2px solid #ffb300',
                  display: 'flex', flexDirection: { xs:'column', md:'row' },
                  alignItems: { xs:'flex-start', md:'center' }, gap: 2,
                }}>
                  <Box sx={{ fontSize: 32, flexShrink: 0 }}>📧</Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100', mb: 0.5 }}>
                      Confirma tu correo para continuar
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5d4037', mb: 0.5 }}>
                      Enviamos un enlace a <strong>{solicitudInfo.emailMask || 'tu correo'}</strong>.
                      Revisa tu bandeja de entrada (y la carpeta de spam).
                      No podrás pagar, agendar ni subir documentos hasta confirmarlo.
                    </Typography>
                    {reenvioEmailState === 'enviado' && (
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        ✓ {reenvioEmailMsg}
                      </Typography>
                    )}
                    {reenvioEmailState === 'error' && (
                      <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>
                        ✗ {reenvioEmailMsg}
                      </Typography>
                    )}
                    {/* Widget Turnstile invisible — se ejecuta al montar el banner */}
                    <TurnstileWidget onToken={setTsTokenReenvio} action="reenviar-confirmacion" />
                  </Box>
                  <Button
                    variant="contained"
                    disabled={reenvioEmailState === 'enviando' || reenvioEmailState === 'enviado' || !tsTokenReenvio}
                    startIcon={reenvioEmailState === 'enviando' ? <CircularProgress size={14} color="inherit"/> : null}
                    onClick={async () => {
                      setReenvioEmailState('enviando');
                      setReenvioEmailMsg('');
                      try {
                        const emailReal = window.prompt(
                          'Para reenviar el correo de confirmación, ingresa tu dirección de correo electrónico completa:',
                          ''
                        );
                        if (!emailReal || !emailReal.includes('@')) {
                          setReenvioEmailState('idle');
                          return;
                        }
                        const resp = await reenviarConfirmacionEmail(emailReal.trim().toLowerCase(), tsTokenReenvio);
                        setReenvioEmailState('enviado');
                        setReenvioEmailMsg(resp?.mensaje || 'Si el correo está registrado, recibirás un nuevo enlace en unos minutos.');
                      } catch (err) {
                        setReenvioEmailState('error');
                        setReenvioEmailMsg(err?.data?.mensaje || 'No se pudo enviar el correo. Intenta de nuevo en un minuto.');
                      }
                    }}
                    sx={{
                      background: '#e65100', '&:hover': { background: '#bf360c' },
                      fontWeight: 700, whiteSpace: 'nowrap',
                    }}>
                    {reenvioEmailState === 'enviando' ? 'Enviando…'
                     : reenvioEmailState === 'enviado' ? 'Enviado ✓'
                     : 'Reenviar correo'}
                  </Button>
                </Box>
              )}

              {steps.map((step, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 3, mb: 3 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, #1a4d2e, #2d7a4a)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 15px rgba(26,77,46,0.3)',
                  }}>
                    <Typography sx={{ color: '#ffffff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                      {step.num}
                    </Typography>
                  </Box>
                  <Box sx={{ pb: i < steps.length - 1 ? 3 : 0, borderBottom: i < steps.length - 1 ? '1px dashed rgba(0,0,0,0.1)' : 'none', flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.8, color: '#1a1a1a' }}>{step.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#718096', mb: 1.5 }}>{step.desc}</Typography>
                    {step.boton && (
                      <>
                      {/* Mensaje contextual según estado del botón */}
                      {step.boton.accion === 'agendar' && solicitudInfo && solicitudInfo.sesionEstado === 'REALIZADA' && (
                        <Typography variant="caption" sx={{ display:'block', color:'#2e7d32', mb:0.5, fontWeight:600 }}>
                          ✓ Sesión ya realizada
                          {solicitudInfo.sesionFecha ? ` el ${new Date(solicitudInfo.sesionFecha).toLocaleDateString('es-CO')}` : ''}
                          {solicitudInfo.sesionMedio ? ` vía ${solicitudInfo.sesionMedio}` : ''}
                        </Typography>
                      )}
                      {step.boton.accion === 'agendar' && solicitudInfo && solicitudInfo.tieneSesionAgendada && solicitudInfo.sesionEstado !== 'REALIZADA' && (
                        <Typography variant="caption" sx={{ display:'block', color:'#1565c0', mb:0.5, fontWeight:600 }}>
                          📅 Sesión agendada
                          {solicitudInfo.sesionFecha ? ` para el ${new Date(solicitudInfo.sesionFecha).toLocaleDateString('es-CO')}` : ''}
                          {solicitudInfo.sesionMedio ? ` vía ${solicitudInfo.sesionMedio}` : ''}
                        </Typography>
                      )}
                      {step.boton.accion === 'guia' && solicitudInfo && solicitudInfo.documentosEnviados && !(solicitudInfo.documentosRechazados > 0) && (
                        <Typography variant="caption" sx={{ display:'block', color:'#2e7d32', mb:0.5, fontWeight:600 }}>
                          ✓ Documentos enviados ({solicitudInfo.documentosAprobados || 0} aprobados)
                        </Typography>
                      )}
                      {step.boton.accion === 'guia' && solicitudInfo && solicitudInfo.documentosRechazados > 0 && (
                        <Typography variant="caption" sx={{ display:'block', color:'#c62828', mb:0.5, fontWeight:600 }}>
                          ⚠ {solicitudInfo.documentosRechazados} documento(s) rechazado(s) — debe resubir
                        </Typography>
                      )}
                      {(step.boton.accion === 'agendar' || step.boton.accion === 'guia') && !solicitudInfo && (
                        <Typography variant="caption" sx={{ display:'block', color:'#bbb', mb:0.5, fontStyle:'italic' }}>
                          Disponible después de enviar su solicitud inicial o consultar su estado
                        </Typography>
                      )}
                      {step.boton.accion === 'agendar' && solicitudInfo &&
                       solicitudInfo.afiliadoId &&
                       !pagoConfirmado && !solicitudInfo.pagoInscripcionConfirmado && (
                        <Box sx={{ mb:2 }}>
                          <PagoInscripcionWidget
                            afiliadoId={solicitudInfo.afiliadoId}
                            nombres={solicitudInfo.nombres}
                            onPagado={() => setPagoConfirmado(true)}
                          />
                        </Box>
                      )}
                      <Button
                        id={step.boton.accion === 'agendar' ? 'btn-agendar-sesion' : step.boton.accion === 'guia' ? 'btn-completar-docs' : undefined}
                        variant={step.boton.variante}
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }}/>}
                        onClick={() => {
                          if (step.boton.accion === 'contrato') {
                            setPreVerifDoc(''); setPreVerifState('idle'); setPreVerifOpen(true);
                          } else if (step.boton.accion === 'estado') {
                            document.getElementById('consulta-estado')?.scrollIntoView({ behavior:'smooth' });
                          } else if (step.boton.accion === 'agendar') {
                            const nombre = solicitudInfo?.nombres || '';
                            const cedula = solicitudInfo?.cedula   || '';
                            const estado = solicitudInfo?.estado   || 'PENDIENTE';
                            const msg = `Hola, soy ${nombre} con cédula ${cedula}. Quiero agendar mi sesión de orientación para la afiliación a FENAGRO APL. Mi solicitud está en estado: ${estado}.`;
                            // 1. Registrar la solicitud en el backend (dispara el correo a info@fenagro-oficial.org)
                            if (solicitudInfo?.afiliadoId) {
                              registrarSesionOrientacion(solicitudInfo.afiliadoId, {
                                fechaSesion: null,
                                duracionMin: null,
                                medioSesion: 'WhatsApp',
                                urlGrabacion: '',
                                notasAdmin: `Solicitud de agendamiento enviada por el afiliado vía portal. CC: ${cedula}`,
                                estado: 'PROGRAMADA',
                              }).catch(() => {});  // fire-and-forget, no bloquea la UX
                            }
                            // 2. Abrir WhatsApp para que el afiliado contacte al equipo
                            window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_ADMIN || "573007765400"}?text=${encodeURIComponent(msg)}`, '_blank');
                          } else if (step.boton.accion === 'guia') {
                            setGuiaOpen(true);
                          }
                        }}
                        disabled={
                          (!solicitudInfo && (step.boton.accion === 'agendar' || step.boton.accion === 'guia'))
                          // Deshabilitar TODOS los pasos posteriores si el email no está confirmado
                          || (solicitudInfo && solicitudInfo.emailConfirmado === false)
                          // Deshabilitar "agendar" si pago pendiente
                          || (step.boton.accion === 'agendar' && solicitudInfo &&
                              !pagoConfirmado && !solicitudInfo.pagoInscripcionConfirmado)
                          // Deshabilitar "agendar" si ya tiene sesión agendada o realizada
                          || (step.boton.accion === 'agendar' && solicitudInfo &&
                              (solicitudInfo.tieneSesionAgendada || solicitudInfo.sesionEstado === 'REALIZADA'))
                          // Deshabilitar "completar documentos" si ya envió todo y no hay rechazos
                          || (step.boton.accion === 'guia' && solicitudInfo &&
                              solicitudInfo.documentosEnviados && !(solicitudInfo.documentosRechazados > 0))
                        }
                        sx={{
                          borderColor: '#1a4d2e', color: '#1a4d2e', borderRadius: 6,
                          fontSize: '0.78rem', px: 2.5,
                          '&:hover': { background: '#1a4d2e', color: '#fff' },
                          '&.Mui-disabled': { borderColor: '#ccc', color: '#bbb' },
                        }}
                      >
                        {step.boton.label}
                      </Button>
                      </>
                    )}
                  </Box>
                </Box>
              ))}

              <Box sx={{
                mt: 4, p: 4, background: 'linear-gradient(135deg, #1a4d2e, #0e2a18)',
                borderRadius: 3, textAlign: 'center',
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', mb: 2 }}>
                  ¿Listo para unirse a la red?
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => { setPreVerifDoc(''); setPreVerifState('idle'); setPreVerifOpen(true); }}
                  sx={{
                    background: 'linear-gradient(135deg, #c8a84b, #e0c870)',
                    color: '#1a1a1a', px: 4,
                    '&:hover': { background: 'linear-gradient(135deg, #9c7e32, #c8a84b)' },
                  }}
                >
                  Iniciar Proceso de Afiliación
                </Button>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', mt: 1.5 }}>
                  fondoemprendimientofenagro@gmail.com · 3007765400
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* ── Pre-verificación de cédula antes del formulario ── */}
      <Dialog open={preVerifOpen} onClose={() => setPreVerifOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color:'#fff',
          display:'flex', alignItems:'center', justifyContent:'space-between', py:2 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <BadgeIcon/>
            <Typography sx={{ fontWeight:700 }}>Verificación previa</Typography>
          </Box>
          <IconButton size="small" onClick={() => setPreVerifOpen(false)} sx={{ color:'rgba(255,255,255,0.7)' }}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt:3, pb:2 }}>
          <Typography variant="body2" sx={{ color:'#555', mb:2.5 }}>
            Antes de iniciar el proceso, ingrese su número de cédula para verificar su elegibilidad.
          </Typography>
          <TextField
            fullWidth size="small" label="Número de cédula" autoFocus
            value={preVerifDoc}
            inputProps={{ inputMode:'numeric', maxLength:12 }}
            onChange={e => {
              const val = e.target.value.replace(/\D/g,'');
              setPreVerifDoc(val);
              setPreVerifState('idle');
            }}
            onKeyDown={e => { if (e.key === 'Enter' && preVerifDoc.trim().length >= 5) verificarElegibilidad(); }}
            error={preVerifState === 'excluido' || preVerifState === 'taken'}
            helperText={
              preVerifState === 'checking' ? '⏳ Verificando…' :
              preVerifState === 'taken'    ? '⏳ Cédula encontrada — cargando su estado de afiliación…' :
              preVerifState === 'excluido' ? 'En este momento no es posible completar su solicitud de afiliación. Para más información contáctenos directamente.' :
              preVerifState === 'ok'       ? '✅ Puede continuar con el proceso.' : ' '
            }
            sx={{ mb:1 }}
          />
          {preVerifState === 'excluido' && (
            <Alert severity="error" icon={<BlockIcon/>} sx={{ mt:1, fontSize:'0.8rem' }}>
              Si cree que hay un error, comuníquese con nosotros directamente.
            </Alert>
          )}

          {/* Turnstile invisible — reemplaza captcha matemático */}
          <TurnstileWidget onToken={setTsTokenPreVerif} action="pre-verificacion" />

        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, flexDirection:'column', gap:1 }}>
          {preVerifState === 'ok' ? (
            <Button fullWidth variant="contained" size="large"
              sx={{ background:'linear-gradient(135deg,#1a4d2e,#43a047)', fontWeight:700, borderRadius:2 }}
              onClick={() => { setPreVerifOpen(false); setContratoOpen(true); }}>
              Continuar con el proceso →
            </Button>
          ) : (
            <Button fullWidth variant="contained" size="large"
              disabled={preVerifDoc.trim().length < 5 || preVerifState === 'checking' || preVerifState === 'excluido' || !tsTokenPreVerif}
              sx={{ background:'linear-gradient(135deg,#1a4d2e,#43a047)', fontWeight:700, borderRadius:2 }}
              onClick={verificarElegibilidad}>
              {preVerifState === 'checking' ? <CircularProgress size={20} color="inherit"/> : 'Verificar elegibilidad'}
            </Button>
          )}
          <Button fullWidth variant="text" size="small" sx={{ color:'#888' }}
            onClick={() => setPreVerifOpen(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal contrato de afiliación */}
      <ContratoAfiliacion
        open={contratoOpen}
        onClose={() => setContratoOpen(false)}
        cooperativaIdFijo={cooperativaIdFijo}
        onSuccess={(info) => setSolicitudInfo(info)}
      />

      {/* ── Diálogo guía de documentos para vinculación ── */}
      <Dialog open={guiaOpen} onClose={() => { setGuiaOpen(false); setGuiaPaso(0); }}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon/>
            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>Envío de documentos — Paso 4: Vinculación</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {solicitudInfo?.nombres && `${solicitudInfo.nombres} · CC ${solicitudInfo.cedula} · `}
                {Object.values(docsGuia).filter(d=>d.valido).length}/6 documentos verificados
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => { setGuiaOpen(false); setGuiaPaso(0); }}
            sx={{ color: '#fff' }}><CloseIcon/></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Stepper activeStep={guiaPaso} orientation="vertical" sx={{ px: 3, pt: 2 }}>
            {[
              { label: 'Cédula de Ciudadanía',        desc: 'Documento vigente — anverso y reverso en una sola imagen o PDF.', icon: '🪪' },
              { label: 'RUT actualizado',              desc: 'Registro Único Tributario expedido por la DIAN con actividad económica relacionada con FENAGRO.', icon: '📄' },
              { label: 'Antecedentes Procuraduría',   desc: 'Certificado de la Procuraduría General de la Nación — vigencia máxima 30 días.', icon: '⚖️' },
              { label: 'Antecedentes Contraloría',    desc: 'Certificado de la Contraloría General de la República — vigencia máxima 30 días.', icon: '🏛️' },
              { label: 'Antecedentes Policía',        desc: 'Certificado judicial de la Policía Nacional — vigencia máxima 30 días.', icon: '👮' },
              { label: 'Certificado bancario',        desc: 'Carta del banco certificando cuenta activa a su nombre para los aportes de la cooperativa.', icon: '🏦' },
            ].map((doc, idx) => {
              const docGuia = docsGuia[idx];
              const valido  = docGuia?.valido === true;
              return (
                <Step key={idx} expanded>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: valido ? '#2e7d32' : guiaPaso === idx ? '#1a4d2e' : '#e8e8e8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: guiaPaso === idx ? '0 0 0 3px rgba(26,77,46,0.2)' : 'none',
                        fontSize: 18, transition: 'all 0.3s',
                      }}>
                        {valido
                          ? <CheckCircleIcon sx={{ fontSize: 20, color: '#fff' }}/>
                          : <span>{doc.icon}</span>
                        }
                      </Box>
                    )}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: guiaPaso >= idx ? 700 : 400, color: guiaPaso >= idx ? '#1a4d2e' : '#aaa', fontSize: '0.95rem' }}>
                        {doc.label}
                      </Typography>
                      {valido && <Chip label="Verificado ✓" size="small" color="success" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}/>}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{doc.desc}</Typography>

                    {/* Verificador con tipo según el documento */}
                    <VerificadorDocumento
                      titulo=""
                      descripcion="Formatos aceptados: PDF, JPG, PNG. Máximo 10MB."
                      tipoDocumento={['cedula','rut','antecedentes_procuraduria','antecedentes_contraloria','antecedentes_policia','bancario'][idx]}
                      cedula={solicitudInfo?.cedula || ''}
                      nombres={solicitudInfo?.nombres || ''}
                      apellidos={solicitudInfo?.apellidos || ''}
                      onValido={(file, resultado) => {
                        setDocGuia(idx, { file, url: resultado?.urlDocumento || null, valido: true });
                        // Guardar en la BD si hay afiliadoId y urlStorage
                        if (solicitudInfo?.afiliadoId && resultado?.urlDocumento) {
                          const tipos = ['cedula','rut','antecedentes_procuraduria',
                            'antecedentes_contraloria','antecedentes_policia','bancario'];
                          guardarDocumentoAfiliado({
                            afiliadoID:    solicitudInfo.afiliadoId,
                            tipoDocumento: tipos[idx],
                            nombreArchivo: file.name,
                            urlStorage:    resultado.urlDocumento,
                            confianzaOcr:  resultado.confianza || null,
                          }).catch(e => console.warn('No se pudo guardar doc en BD:', e));
                        }
                        setTimeout(() => setGuiaPaso(p => Math.min(5, p + 1)), 800);
                      }}
                      onInvalido={() => setDocGuia(idx, { valido: false })}
                    />

                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      {idx > 0 && (
                        <Button size="small" variant="text"
                          onClick={() => setGuiaPaso(p => Math.max(0, p - 1))}
                          sx={{ color: '#888', fontSize: '0.75rem' }}>
                          ← Anterior
                        </Button>
                      )}
                      {valido && idx < 5 && (
                        <Button size="small" variant="outlined"
                          onClick={() => setGuiaPaso(p => Math.min(5, p + 1))}
                          sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontSize: '0.75rem', borderRadius: 4 }}>
                          Siguiente →
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>

          {/* Resumen final */}
          {todosSubidos && (
            <Box sx={{ mx: 3, mb: 3, p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff' }}>
              {notifState === 'sent' ? (
                <>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5 }}>
                    <CheckCircleIcon sx={{ color:'#a5d6a7' }}/>
                    <Typography sx={{ fontWeight:700 }}>¡Notificación enviada!</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.8)', display:'block', mb:1.5 }}>
                    El equipo de FENAGRO APL recibió un correo con sus documentos y lo contactará pronto.
                    También puede escribirnos por WhatsApp para confirmar.
                  </Typography>
                  <Button variant="contained" startIcon={<WhatsAppIcon/>}
                    onClick={() => {
                      const msg = `Hola, soy ${solicitudInfo?.nombres || ''} ${solicitudInfo?.apellidos || ''} CC ${solicitudInfo?.cedula || ''}. Ya subí todos mis documentos de vinculación a FENAGRO APL y notifiqué por correo. Solicitud #${String(solicitudInfo?.afiliadoId || '').padStart(6,'0')}.`;
                      window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_ADMIN || "573007765400"}?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    sx={{ background:'#25D366', color:'#fff', borderRadius:6, fontWeight:700, fontSize:'0.8rem', '&:hover':{ background:'#128C7E' } }}>
                    Confirmar también por WhatsApp
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ fontWeight:700, mb:0.5 }}>🎉 ¡Todos los documentos enviados!</Typography>
                  <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.8)', display:'block', mb:1.5 }}>
                    Sus documentos están listos para revisión. Notifique al equipo de FENAGRO APL para que inicien la validación.
                    Recibirá respuesta por correo electrónico.
                  </Typography>
                  <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
                    <Button variant="contained"
                      disabled={notifState === 'loading' || !solicitudInfo?.afiliadoId}
                      startIcon={notifState === 'loading'
                        ? <CircularProgress size={16} color="inherit"/>
                        : <span>📧</span>}
                      onClick={async () => {
                        setNotifState('loading');
                        try {
                          const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5246';
                          const resp = await fetch(
                            `${BASE}/api/ecolab/documentos-afiliacion/${solicitudInfo.afiliadoId}/notificar-completado`,
                            { method:'POST' }
                          );
                          if (resp.ok) setNotifState('sent');
                          else setNotifState('error');
                        } catch { setNotifState('error'); }
                      }}
                      sx={{ background:'rgba(255,255,255,0.2)', color:'#fff', borderRadius:6, fontWeight:700,
                            border:'1px solid rgba(255,255,255,0.4)', fontSize:'0.8rem',
                            '&:hover':{ background:'rgba(255,255,255,0.3)' },
                            '&.Mui-disabled':{ color:'rgba(255,255,255,0.4)' } }}>
                      {notifState === 'loading' ? 'Enviando notificación...' : 'Notificar por correo al equipo FENAGRO'}
                    </Button>
                    <Button variant="contained" startIcon={<WhatsAppIcon/>}
                      onClick={() => {
                        const msg = `Hola, soy ${solicitudInfo?.nombres || ''} ${solicitudInfo?.apellidos || ''} CC ${solicitudInfo?.cedula || ''}. He subido todos mis documentos de vinculación a FENAGRO APL y estoy listo para el siguiente paso. Solicitud #${String(solicitudInfo?.afiliadoId || '').padStart(6,'0')}.`;
                        window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_ADMIN || "573007765400"}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                      sx={{ background:'#25D366', color:'#fff', borderRadius:6, fontWeight:700, fontSize:'0.8rem', '&:hover':{ background:'#128C7E' } }}>
                      Avisar por WhatsApp
                    </Button>
                  </Box>
                  {notifState === 'error' && (
                    <Typography variant="caption" sx={{ color:'#ffcdd2', display:'block', mt:1 }}>
                      No se pudo enviar el correo. Use WhatsApp o contáctenos directamente.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {Object.values(docsGuia).filter(d => d.valido).length} de 6 documentos verificados
            </Typography>
          </Box>
          <Button onClick={() => { setGuiaOpen(false); setGuiaPaso(0); }} sx={{ color: '#888' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Consulta de estado de solicitud ─── */}
      {afiliadoEnCurso && (
        <Alert severity="warning" sx={{ mb:2, borderRadius:2, fontSize:'0.9rem' }}
          onClose={() => setAfiliadoEnCurso(false)}>
          <strong>Ya tiene una solicitud de afiliación en curso.</strong> Consulte su estado ingresando su número de cédula y resolviendo la verificación de seguridad.
        </Alert>
      )}
      <Box id="consulta-estado"><ConsultaEstadoAfiliacion onEncontrada={(info) => { setSolicitudInfo(info); setAfiliadoIdActual(info.afiliadoId); }} /></Box>
    </Box>
  );
}

// ─── Widget consulta de estado ────────────────────────────────────────────

function ConsultaEstadoAfiliacion({ onEncontrada, autoConsultar, onAutoConsultarDone }) {
  const [documento,       setDocumento]       = useState('');
  const [loading,         setLoading]         = useState(false);
  const [resultado,       setResultado]       = useState(null);
  const [error,           setError]           = useState('');
  const [tsToken,         setTsToken]         = useState('');
  const [multiCoops,      setMultiCoops]      = useState(null);
  const [coopSeleccionada,setCoopSeleccionada]= useState(null);

  // Consulta automática cuando viene de pre-verificación (ya existe solicitud)
  React.useEffect(() => {
    if (!autoConsultar) return;
    setDocumento(autoConsultar);
    setLoading(true); setError(''); setResultado(null);
    consultarEstadoAfiliacion(autoConsultar.trim())
      .then(data => {
        setResultado(data);
        onEncontrada?.({
          cedula:              autoConsultar.trim(),
          nombres:             data.nombres   || '',
          apellidos:           data.apellidos || '',
          estado:              data.estado    || 'PENDIENTE',
          afiliadoId:          data.afiliadoID || null,
          documentosSubidos:   data.documentosSubidos || 0,
          documentosAprobados: data.documentosAprobados || 0,
          documentosRechazados:data.documentosRechazados || 0,
          documentosEnviados:  data.documentosEnviados || false,
          tieneSesionAgendada: data.tieneSesionAgendada || false,
          sesionEstado:        data.sesionEstado || null,
          pagoInscripcionConfirmado: data.pagoInscripcionConfirmado || false,
          pasosCompletados:    data.pasosCompletados || '',
          emailConfirmado:     data.emailConfirmado !== false, // por defecto true si no viene
          emailMask:           data.emailMask || '',
        });
        // Scroll suave al widget de resultado
        setTimeout(() => {
          document.getElementById('consulta-estado')?.scrollIntoView({ behavior:'smooth', block:'start' });
        }, 300);
      })
      .catch(() => setError('No se encontró ninguna solicitud con esa cédula.'))
      .finally(() => { setLoading(false); onAutoConsultarDone?.(); });
  }, [autoConsultar, onEncontrada, onAutoConsultarDone]);

  const ESTADOS = {
    PENDIENTE:  { color: '#e65100', bg: '#fff3e0', label: '⏳ Pendiente de validación' },
    ACTIVO:     { color: '#1b5e20', bg: '#e8f5e9', label: '✅ Afiliación aprobada' },
    RECHAZADO:  { color: '#b71c1c', bg: '#ffebee', label: '❌ Solicitud rechazada' },
    SUSPENDIDO: { color: '#4a148c', bg: '#f3e5f5', label: '⚠️ Suspendido temporalmente' },
  };

  const PASOS = ['Solicitud enviada', 'En revisión', 'Pago verificado', 'Afiliación activa'];

  const handleConsultar = async () => {
    if (!documento.trim()) { setError('Ingrese su número de cédula.'); return; }
    if (!tsToken) { setError('Espere a que se complete la verificación de seguridad.'); return; }
    setLoading(true); setError(''); setResultado(null); setMultiCoops(null); setCoopSeleccionada(null);
    try {
      const data = await consultarEstadoAfiliacion(documento.trim(), undefined, tsToken);
      if (data.multipleAfiliaciones) {
        setMultiCoops(data.afiliaciones);
        setLoading(false);
        return;
      }
      setResultado(data);
      onEncontrada?.({
        cedula:                documento.trim(),
        nombres:               data.nombres   || '',
        apellidos:             data.apellidos || '',
        estado:                data.estado    || 'PENDIENTE',
        afiliadoId:            data.afiliadoID || null,
        documentosSubidos:     data.documentosSubidos || 0,
        documentosAprobados:   data.documentosAprobados || 0,
        documentosRechazados:  data.documentosRechazados || 0,
        documentosEnviados:    data.documentosEnviados || false,
        tieneSesionAgendada:   data.tieneSesionAgendada || false,
        sesionEstado:          data.sesionEstado || null,
        sesionFecha:           data.sesionFecha || null,
        sesionMedio:           data.sesionMedio || null,
        nombreCooperativa:     data.nombreCooperativa || null,
        siglasCooperativa:     data.siglasCooperativa || null,
        pasosCompletados:      data.pasosCompletados  || '',
        pagoInscripcionConfirmado: data.pagoInscripcionConfirmado || false,
      });
    } catch (err) {
      setError(err?.data?.mensaje || 'No se encontró solicitud con ese documento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, background: '#fff' }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="overline" sx={{ color: '#1a4d2e', fontWeight: 700 }}>
            Seguimiento
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mt: 1, fontSize: { xs: '1.6rem', md: '2rem' } }}>
            Consulte su solicitud
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
            Ingrese su número de cédula para ver el estado de su afiliación.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, p: { xs: 3, sm: 4 } }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth label="Número de cédula" size="small"
                value={documento} onChange={e => { setDocumento(e.target.value); setError(''); setResultado(null); }}
                onKeyDown={e => e.key === 'Enter' && handleConsultar()}
                error={!!error} helperText={error}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button fullWidth variant="contained" color="success" onClick={handleConsultar}
                    disabled={loading || !tsToken}
                endIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #1a4d2e, #43a047)',
                  '&.Mui-disabled': { background: '#c8e6c9', color: '#fff' } }}>
                {loading ? 'Buscando…' : 'Consultar'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TurnstileWidget onToken={setTsToken} action="consulta-estado" />
            </Grid>
          </Grid>

          {/* Selector de cooperativa cuando hay múltiples afiliaciones */}
        {multiCoops && !resultado && (
          <Paper elevation={0} sx={{ border:'2px solid #1a4d2e', borderRadius:3, p:3, mt:2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:'#1a4d2e', mb:1 }}>
              🏢 Tiene afiliaciones en varias cooperativas
            </Typography>
            <Typography variant="body2" sx={{ color:'#666', mb:2 }}>
              Seleccione la cooperativa cuyo estado desea consultar:
            </Typography>
            <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
              {multiCoops.map(af => (
                <Button key={af.afiliadoID} variant="outlined" fullWidth
                  sx={{ justifyContent:'space-between', borderRadius:2, py:1.2,
                    borderColor:'#1a4d2e', color:'#1a4d2e',
                    '&:hover':{ background:'#f0fdf0' } }}
                  onClick={async () => {
                    setLoading(true); setCoopSeleccionada(af.cooperativaID);
                    try {
                      // console.log('af objeto:', JSON.stringify(af));
                      const data = await consultarEstadoAfiliacion(documento.trim(), af.cooperativaID);
                      setResultado(data);
                      // console.log('DATA MULTI-COOP:', JSON.stringify(data));
                      setMultiCoops(null);
                      onEncontrada?.({
                        cedula: documento.trim(),
                        nombres: data.nombres || '',
                        apellidos: data.apellidos || '',
                        estado: data.estado || 'PENDIENTE',
                        afiliadoId: data.afiliadoID || null,
                        cooperativaID: af.cooperativaID,
                        documentosSubidos: data.documentosSubidos || 0,
                        documentosAprobados: data.documentosAprobados || 0,
                        documentosRechazados: data.documentosRechazados || 0,
                        tieneSesionAgendada: data.tieneSesionAgendada || false,
                        sesionEstado: data.sesionEstado || null,
                        pagoInscripcionConfirmado: data.pagoInscripcionConfirmado || false,
                        pasosCompletados: data.pasosCompletados || '',
                        nombreCooperativa: data.nombreCooperativa || null,
                        siglasCooperativa: data.siglasCooperativa || null,
                        emailConfirmado: data.emailConfirmado !== false,
                        emailMask: data.emailMask || '',
                      });
                    } catch(e) { setError('Error al consultar.'); }
                    finally { setLoading(false); }
                  }}>
                  <span>{af.siglasCooperativa || af.nombreCooperativa || `Cooperativa #${af.cooperativaID}`}</span>
                  <Chip label={af.estado} size="small"
                    sx={{ background: af.estado === 'ACTIVO' ? '#e8f5e9' : '#fff3e0',
                      color: af.estado === 'ACTIVO' ? '#2e7d32' : '#e65100', fontWeight:700, fontSize:'0.7rem' }}/>
                </Button>
              ))}
            </Box>
          </Paper>
        )}

        {resultado && (() => {
            const est = ESTADOS[resultado.estado] || { color: '#666', bg: '#f5f5f5', label: resultado.estado };
            return (
              <Box sx={{ mt: 3 }}>
                {/* Badge de estado */}
                <Box sx={{ background: est.bg, border: `1.5px solid ${est.color}`, borderRadius: 2, p: 2, mb: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700, color: est.color, fontSize: '1rem' }}>{est.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>{resultado.mensajeEstado}</Typography>
                </Box>

                {/* Progreso de 4 pasos */}
                <Box sx={{ mb: 2.5 }}>
                  {resultado.nombreCooperativa && (
                    <Box sx={{ mb:1.5, p:1.5, borderRadius:2, background:'#e8f5e9', border:'1px solid #a5d6a7',
                      display:'flex', alignItems:'center', gap:1 }}>
                      <Typography variant="body2" sx={{ color:'#1a4d2e' }}>
                        Afiliación en: <strong>{resultado.siglasCooperativa || resultado.nombreCooperativa}</strong>
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444', display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Progreso de vinculación
                  </Typography>
                  {[
                    { label: 'Solicitud enviada',
                      done: true, icono: '📋',
                      info: 'Su solicitud fue recibida y está en revisión.' },
                    { label: 'Evaluación de documentos',
                      done: resultado.pasosCompletados?.includes('2') || ['EN_ORIENTACION','ACTIVO'].includes(resultado.estado), icono: '🔍',
                      info: resultado.pasosCompletados?.includes('2') || ['EN_ORIENTACION','ACTIVO'].includes(resultado.estado)
                        ? 'Evaluación aprobada ✓'
                        : 'El revisor evaluará sus documentos. Le notificaremos el resultado.' },
                    { label: 'Pago de inscripción',
                      done: resultado.pagoInscripcionConfirmado, icono: '💳',
                      info: resultado.pagoInscripcionConfirmado
                        ? `Pago confirmado ✓${resultado.fechaPagoInscripcion ? ' — '+new Date(resultado.fechaPagoInscripcion).toLocaleDateString('es-CO') : ''}`
                        : (resultado.pasosCompletados?.includes('2') || ['EN_ORIENTACION','ACTIVO'].includes(resultado.estado))
                          ? 'Use el botón de pago abajo para realizar el pago de $150.000 COP'
                          : 'Disponible tras la aprobación de la evaluación' },
                    { label: 'Sesión de orientación',
                      done: resultado.sesionEstado === 'REALIZADA', icono: '🎓',
                      info: resultado.sesionEstado === 'REALIZADA'
                        ? `Realizada${resultado.sesionFecha ? ' el '+new Date(resultado.sesionFecha).toLocaleDateString('es-CO') : ''}${resultado.sesionMedio ? ' vía '+resultado.sesionMedio : ''} ✓`
                        : resultado.tieneSesionAgendada
                          ? `Agendada${resultado.sesionFecha ? ' para el '+new Date(resultado.sesionFecha).toLocaleDateString('es-CO') : ''}${resultado.sesionMedio ? ' vía '+resultado.sesionMedio : ''}`
                          : resultado.pagoInscripcionConfirmado
                            ? 'Use el botón de abajo para agendar'
                            : 'Disponible tras confirmar el pago' },
                    { label: 'Envío de documentos',
                      done: resultado.documentosAprobados >= 6, icono: '📄',
                      info: resultado.documentosAprobados >= 6
                        ? 'Todos los documentos aprobados ✓'
                        : resultado.documentosSubidos > 0
                          ? `${resultado.documentosSubidos}/6 subidos${resultado.documentosRechazados > 0 ? ' — '+resultado.documentosRechazados+' rechazado(s)' : ' — pendiente de revisión'}`
                          : resultado.sesionEstado === 'REALIZADA'
                            ? 'Use el botón "Completar documentos" de abajo'
                            : 'Disponible tras completar la orientación' },
                  ].map((paso, i) => (
                    <Box key={i} sx={{ display:'flex', gap:1.5, alignItems:'flex-start', mb:1.2,
                      p:1.2, borderRadius:1.5, background: paso.done ? '#f0fdf0' : '#fafafa',
                      border: `1px solid ${paso.done ? '#c8e6c9' : '#e0e0e0'}` }}>
                      <Box sx={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        background: paso.done ? '#2e7d32' : '#e0e0e0', fontSize:14 }}>
                        {paso.done ? <span style={{color:'#fff',fontWeight:700,fontSize:13}}>✓</span> : paso.icono}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: paso.done ? 700 : 400, fontSize:'0.82rem',
                          color: paso.done ? '#1a4d2e' : '#555' }}>{paso.label}</Typography>
                        {paso.info && <Typography variant="caption" sx={{ color: paso.done ? '#388e3c' : '#888' }}>{paso.info}</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Documentos rechazados — aviso prominente */}
                {resultado.documentosRechazados > 0 && (
                  <Alert severity="error" sx={{ mb:2, fontSize:'0.78rem' }}>
                    <strong>{resultado.documentosRechazados} documento(s) rechazado(s).</strong>{' '}
                    Debe subirlos nuevamente:{' '}
                    {resultado.docsConRechazo?.map(d => (
                      <span key={d.tipoDocumento}>
                        <strong>{d.tipoDocumento.replace(/_/g,' ')}</strong>
                        {d.notasAdmin ? ` (${d.notasAdmin})` : ''}
                        {'; '}
                      </span>
                    ))}
                    Use el botón <strong>"Completar envío de documentos"</strong> para resubir.
                  </Alert>
                )}

                {/* Siguiente paso sugerido con link al botón */}
                {resultado.estado !== 'ACTIVO' && resultado.estado !== 'RECHAZADO' && (() => {
                  const sesionCompleta = resultado.sesionEstado === 'REALIZADA';
                  const pagoConfirmadoRes = resultado.pagoInscripcionConfirmado;
                  const nextAccion = !pagoConfirmadoRes ? 'agendar'
                    : !resultado.tieneSesionAgendada && !sesionCompleta ? 'agendar'
                    : sesionCompleta && (resultado.documentosSubidos < 6 || resultado.documentosRechazados > 0) ? 'guia'
                    : sesionCompleta && !resultado.documentosEnviados ? 'guia'
                    : null;
                  const nextMsg = !pagoConfirmadoRes
                    ? 'Realice el pago de inscripción de $150.000 COP para continuar con su proceso de afiliación.'
                    : !resultado.tieneSesionAgendada && !sesionCompleta
                      ? 'Agende su sesión de orientación con el equipo FENAGRO APL.'
                      : resultado.tieneSesionAgendada && !sesionCompleta
                        ? 'Su sesión de orientación está agendada. El envío de documentos estará disponible tras realizarla.'
                        : sesionCompleta && (resultado.documentosSubidos < 6 || resultado.documentosRechazados > 0)
                          ? 'Complete el envío de sus documentos de vinculación.'
                          : sesionCompleta && !resultado.documentosEnviados
                            ? 'Notifique al equipo que sus documentos están listos.'
                            : null;
                  const btnId = nextAccion === 'agendar' ? 'btn-agendar-sesion'
                    : nextAccion === 'guia' ? 'btn-completar-docs' : null;
                  const scrollToBtn = () => {
                    const el = document.getElementById(btnId);
                    if (!el) return;
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // pulso visual en el botón destino
                    el.style.transition = 'box-shadow 0.2s';
                    el.style.boxShadow  = '0 0 0 4px #43a04788';
                    setTimeout(() => { el.style.boxShadow = ''; }, 1200);
                  };
                  if (!nextMsg) return null;
                  return (
                    <Box sx={{ p:1.5, borderRadius:1.5, background:'#fff8e1', border:'1px solid #ffe082',
                      mb:2, display:'flex', alignItems:'center', justifyContent:'space-between', gap:1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight:700, color:'#e65100', display:'block', mb:0.3 }}>
                          ➡ Siguiente paso:
                        </Typography>
                        <Typography variant="caption" sx={{ color:'#555' }}>{nextMsg}</Typography>
                      </Box>
                      {btnId && (
                        <Box
                          onClick={scrollToBtn}
                          sx={{
                            flexShrink: 0,
                            width: 36, height: 36,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg,#1a4d2e,#43a047)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(46,125,50,0.4)',
                            '@keyframes pulse': {
                              '0%':   { transform: 'scale(1)',    boxShadow: '0 2px 8px rgba(46,125,50,0.4)' },
                              '50%':  { transform: 'scale(1.15)', boxShadow: '0 4px 16px rgba(46,125,50,0.7)' },
                              '100%': { transform: 'scale(1)',    boxShadow: '0 2px 8px rgba(46,125,50,0.4)' },
                            },
                            animation: 'pulse 1.4s ease-in-out infinite',
                            '&:hover': { background: 'linear-gradient(135deg,#2e7d32,#66bb6a)', transform:'scale(1.1)' },
                          }}>
                          <ArrowForwardIcon sx={{ color:'#fff', fontSize:20 }}/>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

                {/* Datos del afiliado */}
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                  {[
                    ['N° de solicitud', `#${resultado.afiliadoID}`],
                    ['Nombre', `${resultado.nombres} ${resultado.apellidos}`],
                    ['Email registrado', resultado.emailMask],
                    ['Ubicación', [resultado.municipio, resultado.departamento].filter(Boolean).join(', ') || '—'],
                    ['Modalidad de aporte', resultado.modalidadAporte],
                    ['Fecha de solicitud', resultado.fechaCreacion ? new Date(resultado.fechaCreacion).toLocaleDateString('es-CO', { year:'numeric',month:'long',day:'numeric'}) : '—'],
                  ].map(([l, v]) => v && (
                    <React.Fragment key={l}>
                      <Grid item xs={5}><Typography variant="caption" sx={{ fontWeight: 600, color: '#888' }}>{l}:</Typography></Grid>
                      <Grid item xs={7}><Typography variant="caption" sx={{ color: '#1a1a1a' }}>{v}</Typography></Grid>
                    </React.Fragment>
                  ))}
                </Grid>

                {resultado.estado === 'PENDIENTE' && (
                  <Alert severity="info" sx={{ mt: 2, fontSize: '0.78rem' }}>
                    Para agilizar la validación, envíe el comprobante de pago de $150.000 COP a{' '}
                    <strong>fondoemprendimientofenagro@gmail.com</strong> con el asunto{' '}
                    <strong>"Comprobante afiliación #{resultado.afiliadoID}"</strong>.
                  </Alert>
                )}
              </Box>
            );
          })()}
        </Paper>
      </Container>
    </Box>
  );
}

export function Contacto() {
  const [formData, setFormData] = useState({ nombre: '', email: '', telefono: '', asunto: '', mensaje: '' });
  const [snackOpen, setSnackOpen] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim() || !formData.email.trim() || !formData.mensaje.trim()) {
      setSendError('Nombre, email y mensaje son obligatorios.');
      return;
    }
    setSending(true); setSendError('');
    try {
      await enviarContacto(formData);
      setSnackOpen(true);
      setFormData({ nombre: '', email: '', telefono: '', asunto: '', mensaje: '' });
    } catch (err) {
      setSendError(err?.data?.mensaje || 'No se pudo enviar. Intente de nuevo.');
    } finally { setSending(false); }
  };

  const contactInfo = [
    { icon: <LocationOnIcon />, label: 'Dirección', value: 'Medellín, Antioquia\nEdificio Santa Elena Centro' },
    { icon: <EmailIcon />, label: 'Correo Electrónico', value: 'fondoemprendimientofenagro@gmail.com' },
    { icon: <PhoneIcon />, label: 'Teléfono / WhatsApp', value: (process.env.REACT_APP_WHATSAPP_ADMIN || '3007765400').replace(/^57/, '') },
  ];

  return (
    <Box>
      <PageHero
        title="Contacto"
        subtitle="Estamos listos para atenderle y resolver todas sus inquietudes."
        breadcrumbs={[{ label: 'Contacto' }]}
      />

      <Box sx={{ py: { xs: 8, md: 14 }, background: '#f8f5ef' }}>
        <Container maxWidth="xl">
          <Grid container spacing={8}>
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 2, display: 'block' }}>Estamos aquí</Typography>
              <Typography variant="h3" sx={{ mb: 3, color: '#1a1a1a', fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                Comuníquese con nosotros
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 5 }}>
                Nuestro equipo está disponible para responder sus preguntas, orientarle en el proceso de 
                afiliación o atender sus solicitudes institucionales.
              </Typography>

              {contactInfo.map((info, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2.5, mb: 3.5 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2, flexShrink: 0,
                    background: 'linear-gradient(135deg, #1a4d2e, #2d7a4a)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '& svg': { color: '#ffffff', fontSize: 22 },
                  }}>
                    {info.icon}
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: '#1a4d2e', fontSize: '0.65rem', letterSpacing: '0.12em' }}>
                      {info.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#2d3748', fontWeight: 500, whiteSpace: 'pre-line' }}>
                      {info.value}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 4 }} />

              <Box sx={{
                p: 3.5, background: 'linear-gradient(135deg, rgba(200,168,75,0.08), rgba(26,77,46,0.08))',
                borderRadius: 3, border: '1px solid rgba(200,168,75,0.25)',
              }}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: '0.95rem', color: '#1a1a1a' }}>
                  Horario de Atención
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  Lunes a Viernes: 8:00 AM – 5:00 PM<br />
                  Sábados: 9:00 AM – 1:00 PM<br />
                  Domingos y festivos: Cerrado
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{
                p: { xs: 3, md: 5 }, background: '#ffffff',
                borderRadius: 4, border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 30px rgba(0,0,0,0.06)',
              }}>
                <Typography variant="h5" sx={{ mb: 3, color: '#1a1a1a', fontSize: '1rem', letterSpacing: '0.05em' }}>
                  Envíenos un Mensaje
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Nombre Completo" name="nombre"
                        value={formData.nombre} onChange={handleChange}
                        required variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Correo Electrónico" name="email" type="email"
                        value={formData.email} onChange={handleChange}
                        required variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Teléfono" name="telefono"
                        value={formData.telefono} onChange={handleChange}
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Asunto" name="asunto"
                        value={formData.asunto} onChange={handleChange}
                        required variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth label="Mensaje" name="mensaje" multiline rows={5}
                        value={formData.mensaje} onChange={handleChange}
                        required variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit" variant="contained" color="primary"
                        size="large" fullWidth disabled={sending}
                        endIcon={sending ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ py: 1.8, fontSize: '0.85rem', letterSpacing: '0.1em' }}
                      >
                        {sending ? 'Enviando…' : 'Enviar Mensaje'}
                      </Button>
                      {sendError && (
                        <Alert severity="error" sx={{ mt: 2, fontSize: '0.82rem' }}>
                          {sendError}
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar open={snackOpen} autoHideDuration={5000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackOpen(false)} sx={{ fontFamily: "'Outfit', sans-serif" }}>
          ¡Mensaje enviado con éxito! Le responderemos pronto.
        </Alert>
      </Snackbar>
    </Box>
  );
}
